import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { loginSchema, type LoginInput } from "@ckgo2/shared/schemas";
import { useAuthStore } from "@/stores/authStore";
import { Eye, EyeOff, Loader2 } from "lucide-react";

export default function LoginPage() {
  const { t } = useTranslation();
  const [showPassword, setShowPassword] = useState(false);
  const navigate = useNavigate();
  const { user, login, isLoading, error, clearError } = useAuthStore();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  // Clear any stale error once on mount (clearError is a stable zustand action).
  // Kept separate from the redirect effect: folding it in made an `isLoading`
  // change re-run clearError and wipe the just-set login error before it rendered.
  useEffect(() => {
    clearError();
  }, [clearError]);

  // Redirect once authenticated
  useEffect(() => {
    if (user && !isLoading) {
      navigate("/");
    }
  }, [user, isLoading, navigate]);

  const onSubmit = async (data: LoginInput) => {
    const success = await login(data.email, data.password);
    if (success) {
      navigate("/");
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-100 px-4 dark:bg-gray-900">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="mb-8 flex flex-col items-center">
          <img src="/logo.svg" alt="CK Go2" className="h-12 w-auto" />
          <p className="mt-4 text-gray-600 dark:text-gray-400">
            {t("shared.login.administration")}
          </p>
        </div>

        {/* Login form */}
        <div className="rounded-xl border border-gray-200 bg-white p-8 shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <h2 className="mb-6 text-xl font-semibold text-gray-900 dark:text-white">
            {t("shared.login.signInTitle")}
          </h2>

          {error && (
            <div className="bg-error-50 text-error-700 dark:bg-error-900/30 dark:text-error-400 mb-4 rounded-lg p-4 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
            {/* Email */}
            <div>
              <label htmlFor="email" className="label">
                {t("common.fields.email")}
              </label>
              <input
                {...register("email")}
                type="email"
                id="email"
                autoComplete="email"
                className={`input ${errors.email ? "input-error" : ""}`}
                placeholder={t("shared.login.emailPlaceholder")}
              />
              {errors.email && (
                <p className="text-error-600 mt-1 text-sm">{errors.email.message}</p>
              )}
            </div>

            {/* Password */}
            <div>
              <label htmlFor="password" className="label">
                {t("shared.login.password")}
              </label>
              <div className="relative">
                <input
                  {...register("password")}
                  type={showPassword ? "text" : "password"}
                  id="password"
                  autoComplete="current-password"
                  className={`input pr-10 ${errors.password ? "input-error" : ""}`}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                </button>
              </div>
              {errors.password && (
                <p className="text-error-600 mt-1 text-sm">{errors.password.message}</p>
              )}
            </div>

            {/* Submit */}
            <button type="submit" disabled={isSubmitting} className="btn-primary w-full">
              {isSubmitting ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  {t("shared.login.signingIn")}
                </>
              ) : (
                t("shared.login.signInButton")
              )}
            </button>
          </form>
        </div>

        {/* Footer */}
        <p className="mt-6 text-center text-sm text-gray-500 dark:text-gray-400">
          {t("shared.login.copyright", { year: new Date().getFullYear() })}
        </p>
      </div>
    </div>
  );
}
